import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { LoaderCircle, X } from 'lucide-react';

import { SelectionRange } from '@/generated/api-client';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/services/utils';

import { InspectingIllustration } from './InspectingIllustration';
import { tokenizeInput } from './tokenize';

const processingSteps = ['Reading the sentence…', 'Understanding the phrase…', 'Writing your card…'];
const maxSentenceLength = 300;

type DialogTag = { id: string; name: string };
export type NewCardTagSelection = { tagIds: string[]; newTagNames: string[] };

export function ContextualCardDialog({
  open,
  initialText,
  creating,
  error,
  allTags = [],
  onOpenChange,
  onSubmit,
}: {
  open: boolean;
  initialText?: string;
  creating: boolean;
  error: string;
  allTags?: DialogTag[];
  onOpenChange: (open: boolean) => void;
  onSubmit: (text: string, selections: SelectionRange[], tags: NewCardTagSelection) => void;
}) {
  const [text, setText] = useState('');
  const [selectedWordIds, setSelectedWordIds] = useState<number[]>([]);
  const [processingStep, setProcessingStep] = useState(0);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTagNames, setNewTagNames] = useState<string[]>([]);
  const [addingTag, setAddingTag] = useState(false);
  const [newTagDraft, setNewTagDraft] = useState('');
  const initialTextRef = useRef(initialText);

  const processing = creating;

  useEffect(() => {
    initialTextRef.current = initialText;
  }, [initialText]);

  useEffect(() => {
    if (open) {
      setText(initialTextRef.current ?? '');
      setSelectedWordIds([]);
      setSelectedTagIds([]);
      setNewTagNames([]);
      setAddingTag(false);
      setNewTagDraft('');
    }
  }, [open]);

  useEffect(() => {
    setSelectedWordIds([]);
  }, [text]);

  useEffect(() => {
    if (!processing) {
      setProcessingStep(0);
      return;
    }

    const timer = window.setInterval(
      () => setProcessingStep((current) => (current + 1) % processingSteps.length),
      2400
    );

    return () => window.clearInterval(timer);
  }, [processing]);

  const tokens = tokenizeInput(text);
  const hasChips = tokens.length >= 1;
  const requiresSelection = tokens.length >= 2 && selectedWordIds.length === 0;
  const selectedWords = tokens.filter((token) => selectedWordIds.includes(token.id));
  const headlineWords = selectedWords.length > 0 ? selectedWords : processing ? tokens : [];
  const headline = headlineWords.map((token) => token.text).join(' ');

  const toggleWord = (id: number) => {
    setSelectedWordIds((current) =>
      current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id]
    );
  };

  const toggleTag = (id: string) => {
    setSelectedTagIds((current) =>
      current.includes(id) ? current.filter((tagId) => tagId !== id) : [...current, id]
    );
  };

  const commitNewTag = () => {
    const trimmed = newTagDraft.trim();
    if (trimmed && !newTagNames.some((name) => name.toLowerCase() === trimmed.toLowerCase())) {
      setNewTagNames((current) => [...current, trimmed]);
    }
    setNewTagDraft('');
    setAddingTag(false);
  };

  const handleSubmitSentence = () => {
    if (!text.trim() || creating) return;
    if (requiresSelection) return;

    const selections = tokens
      .filter((token) => selectedWordIds.includes(token.id))
      .map((token) => new SelectionRange({ start: token.start, length: token.length }));

    onSubmit(text.trim(), selections, { tagIds: selectedTagIds, newTagNames });
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !creating && onOpenChange(nextOpen)}>
      <DialogContent
        showCloseButton={!creating}
        className="top-[8dvh] translate-y-0 gap-0 overflow-hidden rounded-2xl border-white bg-white p-0 sm:top-1/2 sm:max-w-2xl sm:-translate-y-1/2"
      >
        <DialogDescription className="sr-only">
          Type a sentence or choose specific words to create a card.
        </DialogDescription>

        <div className="flex items-start justify-between gap-4 px-[22px] pb-4 pt-5">
          <div className="min-w-0">
            <div className="text-[13px] font-bold uppercase tracking-[0.08em] text-[#a39c8d]">New card</div>
            <DialogTitle
              className={cn(
                'mt-1.5 truncate font-display text-3xl font-semibold leading-[1.15]',
                headline ? 'text-[#1e1c18]' : 'text-slate-400'
              )}
            >
              {headline || 'Type a sentence below'}
            </DialogTitle>
          </div>
        </div>

        {hasChips && (
          <div className="flex flex-wrap gap-[7px] px-[22px] pb-[18px]">
            {tokens.map((token, index) => {
              const selected = selectedWordIds.includes(token.id);
              const dimmed = processing && selectedWordIds.length > 0 && !selected;

              return (
                <button
                  key={`${token.text}-${token.id}`}
                  type="button"
                  disabled={creating}
                  onClick={() => toggleWord(token.id)}
                  aria-pressed={selected}
                  style={processing ? ({ '--chip-index': index } as CSSProperties) : undefined}
                  className={cn(
                    'flex h-9 items-center whitespace-nowrap rounded-full px-4 text-base font-semibold leading-none outline-none transition focus-visible:ring-2 focus-visible:ring-[#2f5c4e]/40',
                    selected
                      ? 'bg-[#2f5c4e] text-white shadow-sm hover:bg-[#274d41]'
                      : 'bg-[#f3f1ec] text-[#3d3a33] hover:bg-[#ece8e0]',
                    dimmed && 'bg-[#f6f4ef] text-[#c3bcae] hover:bg-[#f6f4ef]',
                    processing && 'meaning-chip-scan'
                  )}
                >
                  {token.text}
                </button>
              );
            })}
          </div>
        )}

        {processing ? (
          <div
            className={cn(
              'meaning-inspect-panel flex items-end gap-6 overflow-hidden bg-slate-50 pr-6 pl-9',
              hasChips && 'border-t border-slate-200'
            )}
          >
            <InspectingIllustration className="meaning-inspect-figure mt-6 h-[152px] w-auto shrink-0 text-slate-900" />
            <div className="min-w-0 flex-1 self-center">
              <p key={processingStep} className="meaning-status-line text-[15px] font-semibold text-slate-900">
                {processingSteps[processingStep]}
              </p>
              <p className="mt-1 text-[13px] text-slate-500">Hang tight, this takes a few seconds.</p>
            </div>
          </div>
        ) : (
          <div className={cn('mx-[22px] pb-[18px] pt-1', hasChips && 'border-t border-[#f1eee8] pt-4')}>
            <div className="rounded-[14px] border-[1.5px] border-[#2f5c4e] bg-white px-[15px] py-[13px] shadow-[0_0_0_3px_rgba(47,92,78,0.14)]">
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value.slice(0, maxSentenceLength))}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSubmitSentence();
                  }
                }}
                placeholder="Type or paste a sentence…"
                rows={2}
                autoFocus
                enterKeyHint="send"
                maxLength={maxSentenceLength}
                className="w-full resize-none border-0 bg-transparent font-display text-[17px] leading-[1.55] text-[#2b2821] outline-none placeholder:font-sans placeholder:text-slate-400"
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-2.5 text-[13px] text-[#a39c8d]">
              <span>Paste or type the sentence you met the word in</span>
              <span>{text.length} / {maxSentenceLength}</span>
            </div>

            {allTags.length > 0 && (
              <div className="mt-3.5 flex flex-wrap items-center gap-2.5">
                <span className="shrink-0 text-sm font-semibold text-[#7b7669]">Tag</span>
                {allTags.slice(0, 6).map((tag) => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={cn(
                        'flex h-8 items-center whitespace-nowrap rounded-full px-3.5 text-sm font-semibold transition',
                        selected
                          ? 'bg-[#2f5c4e] text-white'
                          : 'border border-[#e6e2da] text-[#3d3a33] hover:bg-[#f6f4ef]'
                      )}
                    >
                      {tag.name}
                    </button>
                  );
                })}
                {newTagNames.map((name) => (
                  <span
                    key={name}
                    className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full bg-[#2f5c4e] pl-3.5 pr-2 text-sm font-semibold text-white"
                  >
                    {name}
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => setNewTagNames((current) => current.filter((item) => item !== name))}
                      className="rounded-full p-0.5 opacity-80 hover:opacity-100"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                {addingTag ? (
                  <input
                    autoFocus
                    value={newTagDraft}
                    maxLength={50}
                    onChange={(event) => setNewTagDraft(event.target.value)}
                    onBlur={commitNewTag}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        commitNewTag();
                      }
                      if (event.key === 'Escape') {
                        setNewTagDraft('');
                        setAddingTag(false);
                      }
                    }}
                    placeholder="Tag name"
                    className="h-8 w-32 rounded-full border border-dashed border-[#d9d3c7] bg-transparent px-3.5 text-sm font-semibold text-[#3d3a33] outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setAddingTag(true)}
                    className="flex h-8 items-center whitespace-nowrap rounded-full border border-dashed border-[#d9d3c7] px-3.5 text-sm font-semibold text-[#a39c8d] hover:text-[#7b7669]"
                  >
                    + New tag
                  </button>
                )}
              </div>
            )}
          </div>
        )}


        {error && (
          <div role="alert" className="border-t border-red-100 bg-red-50 px-5 py-2.5 text-xs text-red-700">
        {error}
          </div>
        )}

        <div
          className={cn(
        'flex items-center justify-between gap-3 border-t border-[#f1eee8] bg-[#faf8f4] px-[22px] py-3.5',
        processing && 'hidden'
          )}
        >
          <div className="text-sm text-[#a39c8d]">The card opens right after creation</div>
          <div className="ml-auto flex items-center gap-2.5">
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={creating}
          className="flex h-11 items-center px-4 text-[15px] font-semibold text-[#57534a] transition hover:text-[#1e1c18]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmitSentence}
          disabled={
            creating || !text.trim() || requiresSelection
          }
          className={cn(
            'flex h-11 items-center gap-2 whitespace-nowrap rounded-full bg-[#0f1115] px-6 text-[15px] font-bold text-white transition hover:bg-[#1e2126]',
            (creating || !text.trim() || requiresSelection) && 'pointer-events-none opacity-50'
          )}
        >
          {creating && <LoaderCircle className="size-4 animate-spin" />}
          {'Create card'}
        </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
